import { needsPerson, type SortDir, type SortKey } from './listView';
import type { TaskNarrowing, WorkspaceTaskRow } from '@/features/tasks';

/**
 * What the list has been narrowed to. Every member absent means everything.
 *
 * `TaskNarrowing`'s, not its own. This used to be a separate interface with the
 * same three members, and the separation is how the board came to have no text
 * search and Overview neither text nor state: each page could only express the
 * narrowing its own feature package had declared. The alias stays because this
 * package's functions read better named after what they narrow.
 */
export type TaskSearchNarrowing = TaskNarrowing;

/**
 * The rows in order: by a chosen column (spec 112), or — by default and as
 * every column's tie-break — most recently changed first, then by task.
 *
 * The tie-break is not decoration. While any task has a run in flight this
 * list re-renders behind a two-second poll (spec 110), and
 * `Array.prototype.sort` gives no guarantee about elements it considers equal —
 * so two tasks sharing an `updated_at` would be free to swap places between
 * renders, visibly, while someone was reading them.
 *
 * Returns a new array: the caller's list is derived data it may hold across
 * renders, and sorting it in place would mutate something it did not expect to
 * own.
 */
export function orderTaskRows(
  rows: readonly WorkspaceTaskRow[],
  sort?: { key: SortKey; dir: SortDir },
  now: number = Date.now(),
): WorkspaceTaskRow[] {
  return [...rows].sort((a, b) => {
    if (sort) {
      const byColumn = compareBy(sort, a, b, now);
      if (byColumn !== 0) return byColumn;
    }
    return byDefault(a, b);
  });
}

/** The default order, and every column's tie-break: newest change, then task. */
function byDefault(a: WorkspaceTaskRow, b: WorkspaceTaskRow): number {
  const byTime = b.updatedAt.localeCompare(a.updatedAt);
  return byTime !== 0 ? byTime : a.taskId.localeCompare(b.taskId);
}

/** Creation to finish, or to `now` for a task still going. */
export function leadMs(row: WorkspaceTaskRow, now: number): number {
  const end = row.finishedAt === null ? now : Date.parse(row.finishedAt);
  return end - Date.parse(row.createdAt);
}

/**
 * Most urgent first (FR-012): errors, spend limit, needs a person, in
 * progress, completed, the rest — the order a monitoring reader works in.
 */
function stateRank(row: WorkspaceTaskRow): number {
  if (row.state === 'failed') return 0;
  if (row.blocked !== null) return 1;
  if (needsPerson(row)) return 2;
  // Queued beside running: both are work under way that needs nobody (spec 115).
  if (row.state === 'running' || row.queued !== null) return 3;
  if (row.finished) return 4;
  return 5;
}

/** The value a column orders by; `null` for "nothing to order by". */
function sortValue(
  key: SortKey,
  row: WorkspaceTaskRow,
  now: number,
): string | number | null {
  switch (key) {
    case 'task':
      return row.title.toLocaleLowerCase();
    case 'state':
      return stateRank(row);
    case 'assignee':
      return row.assignee
        ? (
            row.assignee.display_name.trim() || row.assignee.email
          ).toLocaleLowerCase()
        : null;
    case 'created':
      return Date.parse(row.createdAt);
    case 'leadTime':
      return leadMs(row, now);
    case 'cost':
      // A floor orders by the part that is known; a task that never ran, or
      // whose cost nobody can state, has nothing to order by.
      return row.cost.kind === 'exact' || row.cost.kind === 'atLeast'
        ? Number(row.cost.amount)
        : null;
    case 'updated':
      return Date.parse(row.updatedAt);
  }
}

/**
 * One column, one direction — with the parts that must not reverse kept out
 * of the direction (research R5): a row with nothing to order by goes last
 * whichever way the column runs, and equal rows fall through to the default
 * order, which is the same in both directions.
 */
function compareBy(
  sort: { key: SortKey; dir: SortDir },
  a: WorkspaceTaskRow,
  b: WorkspaceTaskRow,
  now: number,
): number {
  const va = sortValue(sort.key, a, now);
  const vb = sortValue(sort.key, b, now);
  if (va === null || vb === null) {
    return va === vb ? 0 : va === null ? 1 : -1;
  }
  const raw =
    typeof va === 'string' && typeof vb === 'string'
      ? va.localeCompare(vb)
      : Number(va) - Number(vb);
  return sort.dir === 'asc' ? raw : -raw;
}
